import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { ContentItem } from '../types';

export function useContent(siteId: string | undefined, contentType?: string) {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = contentType
        ? await api.listContent(siteId, contentType)
        : await api.listAllContent(siteId);
      // Sort by sortOrder
      data.sort((a, b) => a.sortOrder - b.sortOrder);
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [siteId, contentType]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const create = useCallback(
    async (data: Record<string, unknown>, sortOrder?: number) => {
      if (!siteId || !contentType) throw new Error('Missing siteId or contentType');
      const item = await api.createContent(siteId, contentType, data, sortOrder);
      setItems((prev) => [...prev, item].sort((a, b) => a.sortOrder - b.sortOrder));
      return item;
    },
    [siteId, contentType],
  );

  const update = useCallback(
    async (id: string, data: Record<string, unknown>, sortOrder?: number) => {
      if (!siteId || !contentType) throw new Error('Missing siteId or contentType');
      const updated = await api.updateContent(siteId, contentType, id, data, sortOrder);
      setItems((prev) =>
        prev.map((item) => {
          const itemId = item.contentKey.split('#')[1];
          return itemId === id ? updated : item;
        }),
      );
      return updated;
    },
    [siteId, contentType],
  );

  const remove = useCallback(
    async (id: string) => {
      if (!siteId || !contentType) throw new Error('Missing siteId or contentType');
      await api.deleteContent(siteId, contentType, id);
      setItems((prev) =>
        prev.filter((item) => {
          const itemId = item.contentKey.split('#')[1];
          return itemId !== id;
        }),
      );
    },
    [siteId, contentType],
  );

  const reorder = useCallback(
    async (orderedItems: { id: string; sortOrder: number }[]) => {
      if (!siteId || !contentType) throw new Error('Missing siteId or contentType');
      // Optimistic update
      setItems((prev) => {
        const updated = prev.map((item) => {
          const itemId = item.contentKey.split('#')[1];
          const newOrder = orderedItems.find((o) => o.id === itemId);
          return newOrder ? { ...item, sortOrder: newOrder.sortOrder } : item;
        });
        return updated.sort((a, b) => a.sortOrder - b.sortOrder);
      });
      await api.reorderContent(siteId, contentType, orderedItems);
    },
    [siteId, contentType],
  );

  return { items, loading, error, refresh: fetchContent, create, update, remove, reorder };
}
